import SockJS from 'sockjs-client';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';

/**
 * 참여 알림 메시지
 */
export interface EnrollmentMessage {
  scheduleId: number;
  nickname: string;
  characterName: string;
  characterClass: string;
  timestamp: string;
}

/**
 * 뷰어 메시지
 */
export interface ViewMessage {
  nickname: string;
  scheduleId: number;
  timestamp: string;
  action: 'viewing' | 'left';
}

/**
 * 접속자 수 메시지
 */
export interface UserCountMessage {
  count: number;
}

/**
 * 접속자 목록 메시지
 */
export interface UserListMessage {
  users: string[];
}

/**
 * 스케줄 알림 메시지
 */
export interface ScheduleNotificationMessage {
  type: 'created' | 'deleted' | 'enrolled' | 'cancelled';
  scheduleId: number;
  raidTitle: string;
  nickname: string;
  characterName?: string;
  timestamp: string;
}

/**
 * WebSocket 서비스
 * STOMP over SockJS를 사용한 실시간 메시징
 */
class WebSocketService {
  private client: Client | null = null;
  private connected: boolean = false;

  /**
   * WebSocket 연결
   */
  connect(): void {
    if (this.client && this.connected) {
      return;
    }

    const WS_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const wsEndpoint = `${WS_URL}/ws`;

    const socket = new SockJS(wsEndpoint);

    this.client = new Client({
      webSocketFactory: () => socket as any,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        this.connected = true;
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        this.connected = false;
      },
      onDisconnect: () => {
        this.connected = false;
      },
      onWebSocketError: (error) => {
        console.error('WebSocket error:', error);
      },
    });

    this.client.activate();
  }

  /**
   * WebSocket 연결 해제
   */
  disconnect(): void {
    if (this.client) {
      this.client.deactivate();
      this.connected = false;
    }
  }

  /**
   * 연결 상태 확인
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * 스케줄 참여 알림 구독
   * @param scheduleId 스케줄 ID
   * @param callback 메시지 수신 콜백
   */
  subscribeToScheduleEnrollments(
    scheduleId: number,
    callback: (message: EnrollmentMessage) => void
  ): StompSubscription | undefined {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    return this.client.subscribe(
      `/topic/schedule/${scheduleId}/enrollments`,
      (message: IMessage) => {
        const data: EnrollmentMessage = JSON.parse(message.body);
        callback(data);
      }
    );
  }

  /**
   * 스케줄 뷰어 구독 (하이라이트)
   * @param scheduleId 스케줄 ID
   * @param callback 뷰어 목록 수신 콜백
   */
  subscribeToScheduleViewers(
    scheduleId: number,
    callback: (viewers: string[]) => void
  ): StompSubscription | undefined {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    return this.client.subscribe(
      `/topic/schedule/${scheduleId}/viewers`,
      (message: IMessage) => {
        const viewers: string[] = JSON.parse(message.body);
        callback(viewers);
      }
    );
  }

  /**
   * 접속자 수 구독
   * @param callback 접속자 수 수신 콜백
   */
  subscribeToUserCount(
    callback: (count: number) => void
  ): StompSubscription | undefined {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    return this.client.subscribe('/topic/users/count', (message: IMessage) => {
      try {
        const data: UserCountMessage = JSON.parse(message.body);
        callback(data.count);
      } catch (error) {
        console.error('Failed to parse user count message:', error, 'Body:', message.body);
      }
    });
  }

  /**
   * 접속자 목록 구독
   * @param callback 접속자 목록 수신 콜백
   */
  subscribeToUserList(
    callback: (users: string[]) => void
  ): StompSubscription | undefined {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    return this.client.subscribe('/topic/users/list', (message: IMessage) => {
      try {
        const data: UserListMessage = JSON.parse(message.body);
        callback(data.users);
      } catch (error) {
        console.error('Failed to parse user list message:', error, 'Body:', message.body);
      }
    });
  }

  /**
   * 사용자 등록 (연결 후 닉네임 전송)
   * @param nickname 사용자 닉네임
   */
  registerUser(nickname: string): void {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    this.client.publish({
      destination: '/app/user/register',
      body: JSON.stringify({ nickname }),
    });
  }

  /**
   * 스케줄 알림 구독
   * @param callback 알림 수신 콜백
   */
  subscribeToNotifications(
    callback: (notification: ScheduleNotificationMessage) => void
  ): StompSubscription | undefined {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    return this.client.subscribe('/topic/notifications', (message: IMessage) => {
      const notification: ScheduleNotificationMessage = JSON.parse(message.body);
      callback(notification);
    });
  }

  /**
   * 스케줄 보기 알림 전송
   * @param scheduleId 스케줄 ID
   * @param nickname 사용자 닉네임
   * @param action 'viewing' 또는 'left'
   */
  notifyScheduleView(
    scheduleId: number,
    nickname: string,
    action: 'viewing' | 'left'
  ): void {
    if (!this.client || !this.connected) {
      console.warn('WebSocket not connected');
      return;
    }

    const message: ViewMessage = {
      nickname,
      scheduleId,
      timestamp: new Date().toISOString(),
      action,
    };

    this.client.publish({
      destination: `/app/schedule/${scheduleId}/view`,
      body: JSON.stringify(message),
    });
  }
}

// 싱글톤 인스턴스
export const wsService = new WebSocketService();
